import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, X } from "lucide-react";
import { SourcePicker } from "@/components/creatives/SourcePicker";
import { CreativeGallery } from "@/components/creatives/CreativeGallery";
import { ChatPanel } from "@/components/creatives/ChatPanel";
import { LogoSetting } from "@/components/creatives/LogoSetting";
import { CostWidget } from "@/components/creatives/CostWidget";
import { BrandKit } from "@/components/creatives/BrandKit";

export const CR_API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;

export default function Creatives() {
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState([]);
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tick, setTick] = useState(0);
  const [editAsset, setEditAsset] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const prev = useRef(null);

  const load = async () => {
    try {
      const [g, j] = await Promise.all([axios.get(CR_API), axios.get(`${CR_API}/jobs`)]);
      const its = g.data.items || [], js = j.data.items || [];
      const run = js.filter((x) => x.status === "running").length;
      if (prev.current && its.length > prev.current.n) {
        setSelected([]); setProduct(null); setResetKey((k) => k + 1);
      }
      prev.current = { run, n: its.length };
      setItems(its);
      setJobs(js);
      setTick((t) => t + 1);
    } catch (e) { toast.error("Eroare la încărcare: " + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);
  const running = jobs.some((j) => j.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running]);

  const dismiss = async (id) => { await axios.delete(`${CR_API}/jobs/${id}`); load(); };

  return (
    <div className="space-y-6 p-6" data-testid="creatives-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-100"><Sparkles className="h-6 w-6 text-amber-400" /> Creative Ads</h1>
          <p className="mt-1 text-sm text-zinc-400">Imagini 1:1, 4:5 și 9:16 (Nano Banana Pro / 2, GPT Image 2, Seedream 4.5) și video scurte (Kling 3 Pro) din pozele reale ale produselor.</p>
        </div>
        <div className="flex flex-wrap gap-3"><CostWidget refreshKey={tick} /><BrandKit /><LogoSetting /></div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <SourcePicker key={resetKey} product={product} setProduct={setProduct} selected={selected} setSelected={setSelected} />
        <ChatPanel product={product} selected={selected} onChanged={load} editAsset={editAsset} setEditAsset={setEditAsset} />
      </div>
      {jobs.length > 0 && (
        <div className="space-y-2" data-testid="creative-jobs">
          {jobs.map((j) => (
            <div key={j.id} data-testid={`creative-job-${j.id}`} className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm ${j.status === "running" ? "border-amber-500/30 bg-amber-500/5 text-amber-200" : "border-rose-500/30 bg-rose-500/5 text-rose-200"}`}>
              {j.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              <span className="flex-1 truncate">
                {j.type === "video" ? "Video" : j.type === "upscale" ? "Mărire 4K" : j.type === "music" ? "Muzică" : j.type === "textfx" ? "Text animat" : "Imagini"} · {j.product_title || j.sku || "produs"} — {j.status === "running" ? (j.type === "video" ? "se generează (2–5 min)…" : "se generează (~1 min)…") : `eșuat: ${j.error}`}
              </span>
              {j.status === "failed" && <button data-testid={`dismiss-job-${j.id}`} onClick={() => dismiss(j.id)} className="text-xs underline">Închide</button>}
            </div>
          ))}
        </div>
      )}
      <CreativeGallery items={items} reload={load} onEdit={(a) => { setEditAsset(a); document.querySelector('[data-testid="chat-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }} />
    </div>
  );
}
